import { ActionIcon, Menu, ThemeIcon } from "@mantine/core";
import IconCardPlus from "@src/assets/icons/lucide/card-plus.svg?react";
import IconAdjustmentsHorizontal from "@src/assets/icons/tabler/adjustments-horizontal.svg?react";
import IconCaretDownFilled from "@src/assets/icons/tabler/caret-down.svg?react";
import IconCaretRightFilled from "@src/assets/icons/tabler/caret-right.svg?react";
import IconFolderPlus from "@src/assets/icons/tabler/folder-plus.svg?react";
import IconPlus from "@src/assets/icons/tabler/plus.svg?react";
import { shareKeySignal } from "@src/auth/auth-utils";
import { useCreateCardInWorkspace } from "@src/components/board/mutations/create-card-in-workspace";
import { useCreateWhiteboard } from "@src/components/board/mutations/create-whiteboard";
import { fetchActiveWhiteboardsQueryOptions } from "@src/components/board/queries/fetch-active-whiteboards";
import {
	fetchNavbarBranchQueryOptions,
	findBranchKeysContainingInSection,
} from "@src/components/board/queries/fetch-ranklist";
import { useWhiteboardNavigation } from "@src/components/board/use-whiteboard-navigation";
import { generateWhiteboardName } from "@src/components/board/utils";
import {
	WhiteboardDocumentId,
	parseWhiteboardDocumentId,
} from "@src/components/collaboration/document-id";
import { useMyCardLibrary } from "@src/components/library/queries/my-card-library";
import { useCreateBoardModal } from "@src/components/modals/create-board/use-create-board-modal";
import { FeatureGate } from "@src/components/plan/feature-gate/feature-gate";
import { NativeLikeTooltip as Tooltip } from "@src/components/ui/native-like-tooltip";
import { useAuthenticatedUser } from "@src/components/user/user-context";
import { NavbarSection, SupportedFeature } from "@src/gql/graphql";
import { useIsDesktop } from "@src/hooks/use-is-desktop";
import { Tree } from "@src/lib/clean-tree";
import { captureBoardCreationSucceeded } from "@src/utils/analytics";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { nanoid } from "nanoid";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavPendingRename } from "../context/nav-pending-rename-context";
import { useSearch } from "../context/search-context";
import { NavSectionLoadingItem } from "../nav-section-panel/nav-section-loading-item";
import NavSectionTextLabel from "../nav-section-panel/nav-section-text-label";
import { ManageBoardsModal } from "./manage-boards-modal";
import { MoveToFolderModal } from "./move-to-folder-modal";
import { createNavTreeItemRenderer } from "./nav-tree-item";
import { TREE_INDENT_PER_LEVEL_PX, readTreeOpenState } from "./tree-indent";
import { useNavBranchItems, useRanklistTree } from "./use-ranklist-tree";

const OPEN_STATE_KEY = "nav-tree-open-state";

function CreateWhiteboardButton({
	disabled,
	compact = false,
	section = NavbarSection.MyWhiteboards,
}: {
	disabled?: boolean;
	compact?: boolean;
	section?: NavbarSection;
}) {
	const isDesktop = useIsDesktop();
	const authenticatedUser = useAuthenticatedUser();
	const { open: openCreateBoardModal } = useCreateBoardModal();
	const { navigateToWhiteboard } = useWhiteboardNavigation();
	const { mutateAsync: createWhiteboard, isPending: isCreatingWhiteboard } =
		useCreateWhiteboard();
	const isBusy = isCreatingWhiteboard;
	const isSharedSection = section === NavbarSection.SharedWithMe;

	const { handleCreateFolder, isCreatingFolder } = useRanklistTree(section);
	const { requestRename } = useNavPendingRename();

	const { mutateAsync: createCardInWorkspace, isPending: isCreatingCard } =
		useCreateCardInWorkspace();
	const { data: libraryCards } = useMyCardLibrary();
	const highestCardIndexRef = useRef(0);

	const [manageBoardsOpen, setManageBoardsOpen] = useState(false);

	const { data: activeWhiteboards } = useQuery(
		fetchActiveWhiteboardsQueryOptions(
			authenticatedUser.id,
			shareKeySignal.value,
		),
	);

	const highestBoardIndexRef = useRef(0);

	const handleCreateBlankBoard = async () => {
		if (isBusy) return;

		let maxIndex = activeWhiteboards?.length ?? 0;
		if (activeWhiteboards) {
			for (const wb of activeWhiteboards) {
				const match = wb.name?.match(/\d+/);
				if (match) {
					const num = Number.parseInt(match[0], 10);
					if (!Number.isNaN(num) && num > maxIndex) {
						maxIndex = num;
					}
				}
			}
		}
		const nextIndex = Math.max(maxIndex, highestBoardIndexRef.current) + 1;
		highestBoardIndexRef.current = nextIndex;

		const { data } = await createWhiteboard({
			id: new WhiteboardDocumentId(),
			name: generateWhiteboardName(nextIndex),
		});

		if (data?.id) {
			captureBoardCreationSucceeded(
				parseWhiteboardDocumentId(data.id),
				"blank",
			);
			navigateToWhiteboard(data.id);
		}
	};

	const handleCreateNewFolder = async () => {
		const createdFolderId = await handleCreateFolder(null, {
			id: nanoid(),
			isFolder: true,
			name: "New Folder",
		});
		if (createdFolderId) {
			requestRename(createdFolderId);
		}
	};

	const handleCreateNewCard = async () => {
		const newCardId = `Card-${nanoid()}`;
		let maxIndex = libraryCards?.length ?? 0;
		if (libraryCards) {
			for (const card of libraryCards) {
				const match = card.name?.match(/\d+/);
				if (match) {
					const num = Number.parseInt(match[0], 10);
					if (!Number.isNaN(num) && num > maxIndex) {
						maxIndex = num;
					}
				}
			}
		}
		const nextIndex = Math.max(maxIndex, highestCardIndexRef.current) + 1;
		highestCardIndexRef.current = nextIndex;

		const createdCard = await createCardInWorkspace({
			section,
			name: `Card #${nextIndex}`,
			cardId: newCardId,
		});
		if (createdCard?.key) {
			requestRename(createdCard.key);
		}
	};

	const button = compact ? (
		<button
			type="button"
			disabled={isBusy || !!disabled}
			onClick={(event) => event.stopPropagation()}
			aria-label="Create"
			className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-inverse transition-colors bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 cursor-pointer"
		>
			<IconPlus className="h-4 w-4 shrink-0" />
		</button>
	) : (
		<button
			type="button"
			disabled={isBusy || !!disabled}
			onClick={(event) => event.stopPropagation()}
			aria-label="Create"
			className="flex h-8 w-full items-center justify-center gap-1.5 rounded-lg px-3 text-sm font-semibold text-text-inverse transition-colors bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 cursor-pointer"
		>
			<IconPlus className="h-3 w-3 shrink-0" />
			Create
		</button>
	);

	if (disabled) {
		return <Tooltip label="Upgrade to create more boards ✨">{button}</Tooltip>;
	}

	return (
		<>
			<Menu
				position={isDesktop ? "right-start" : "bottom"}
				shadow="md"
				radius="md"
				withinPortal
				zIndex={3100}
			>
				{compact ? (
					<Tooltip label="Create" openDelay={300} withArrow>
						<Menu.Target>{button}</Menu.Target>
					</Tooltip>
				) : (
					<Menu.Target>{button}</Menu.Target>
				)}
				<Menu.Dropdown>
					<Menu.Item
						leftSection={<IconPlus width={15} height={15} />}
						onClick={handleCreateBlankBoard}
						disabled={isBusy}
					>
						New board
					</Menu.Item>
					<Menu.Item
						leftSection={<IconFolderPlus width={15} height={15} />}
						onClick={handleCreateNewFolder}
						disabled={isCreatingFolder}
					>
						New folder
					</Menu.Item>
					{!isSharedSection && (
						<Menu.Item
							leftSection={<IconCardPlus width={15} height={15} />}
							onClick={handleCreateNewCard}
							disabled={isCreatingCard}
						>
							New card
						</Menu.Item>
					)}
					<Menu.Divider />
					<Menu.Item
						leftSection={<IconAdjustmentsHorizontal width={15} height={15} />}
						onClick={() => setManageBoardsOpen(true)}
					>
						{isSharedSection ? "Manage shared workspace" : "Manage workspace"}
					</Menu.Item>
					<Menu.Item onClick={openCreateBoardModal} disabled={isBusy}>
						Create from examples
					</Menu.Item>
				</Menu.Dropdown>
			</Menu>

			<ManageBoardsModal
				opened={manageBoardsOpen}
				onClose={() => setManageBoardsOpen(false)}
				section={section}
			/>
		</>
	);
}

export function NavTreeCreateBoardRow({
	compact = false,
	section,
}: {
	compact?: boolean;
	section?: NavbarSection;
}) {
	return (
		<FeatureGate
			feature={SupportedFeature.CreateBoards}
			render={({ enabled, stats }) => (
				<CreateWhiteboardButton
					disabled={!enabled || stats?.available === false}
					compact={compact}
					section={section}
				/>
			)}
		/>
	);
}

export function NavTreePanel({
	hideSectionHeader = false,
}: {
	hideSectionHeader?: boolean;
} = {}) {
	const queryClient = useQueryClient();
	const {
		handleMoveItem,
		handleCreateFolder,
		handleDeleteFolder,
		isCreatingFolder,
	} = useRanklistTree();
	const { pendingRenameItemId, requestRename, consumeRename } =
		useNavPendingRename();
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [initialOpenState] = useState(() => readTreeOpenState(OPEN_STATE_KEY));

	const { items: rootItems, isLoading } = useNavBranchItems(null);
	const { query: navSearchQuery } = useSearch();
	const treeDragAndDropEnabled = !navSearchQuery.trim();

	const { data: favouriteBranch } = useQuery(
		fetchNavbarBranchQueryOptions(NavbarSection.Favourites, null),
	);
	const favouriteKeys = useMemo(
		() => new Set((favouriteBranch ?? []).map((item) => item.key)),
		[favouriteBranch],
	);

	const [moveModalItemId, setMoveModalItemId] = useState<string | null>(null);
	const [moveModalSourceParent, setMoveModalSourceParent] = useState<
		string | null
	>(null);
	const [manageBoardsOpen, setManageBoardsOpen] = useState(false);

	const { mutateAsync: createCardInWorkspace, isPending: isCreatingCard } =
		useCreateCardInWorkspace();
	const { data: libraryCards } = useMyCardLibrary();
	const highestCardIndexRef = useRef(0);

	const navbarSection = NavbarSection.MyWhiteboards;

	const handleMoveToFolder = useCallback(
		(itemId: string) => {
			// Find which branch cache contains this item to get its current parent.
			// Branch query keys follow `navbarBranchQueryKeys.branch(section, parentKey)`
			// where parentKey is null for root-level items or a folder key for nested items.
			const branchKeys = findBranchKeysContainingInSection(
				queryClient,
				itemId,
				navbarSection,
			);
			const rawParentKey = branchKeys[0]?.[2] as string | undefined;
			const sourceParent =
				rawParentKey === "root" || rawParentKey == null ? null : rawParentKey;
			setMoveModalSourceParent(sourceParent);
			setMoveModalItemId(itemId);
		},
		[queryClient, navbarSection],
	);

	const handleDeleteFolderFromMenu = useCallback(
		(folderId: string) => {
			handleDeleteFolder(folderId, null);
		},
		[handleDeleteFolder],
	);

	const renderNavTreeItem = useMemo(
		() =>
			createNavTreeItemRenderer({
				favouriteKeys,
				onDeleteFolder: handleDeleteFolderFromMenu,
				onMoveToFolder: handleMoveToFolder,
				activeSource: "my-boards",
				navbarSection,
				showMoveToFolder: true,
				pendingRenameItemId,
				onPendingRenameHandled: consumeRename,
			}),
		[
			favouriteKeys,
			handleDeleteFolderFromMenu,
			handleMoveToFolder,
			navbarSection,
			pendingRenameItemId,
			consumeRename,
		],
	);

	if (isLoading) {
		return <NavSectionLoadingItem />;
	}

	return (
		<div id="nav-tree-panel" className="rounded-lg w-0 min-w-full">
			{hideSectionHeader ? null : (
				<div
					className="inline-flex items-center justify-center max-w-full min-w-full cursor-pointer whitespace-nowrap gap-0.5 h-8 bg-surface-default sticky top-0 z-10"
					onClick={() => setIsCollapsed((v) => !v)}
				>
					<ThemeIcon
						variant="transparent"
						size="xs"
						color="var(--ui-color-text-primary)"
					>
						{!isCollapsed ? <IconCaretDownFilled /> : <IconCaretRightFilled />}
					</ThemeIcon>

					<NavSectionTextLabel
						className="grow pointer-events-auto select-none max-w-full ml-2 font-bold text-sm sm:text-xs text-text-secondary uppercase"
						label="My Workspace"
						size="sm"
					/>

					<Tooltip
						label="Manage workspace"
						openDelay={200}
						zIndex={9999}
						withArrow
					>
						<ActionIcon
							variant="subtle"
							size="md"
							color="var(--ui-color-text-muted)"
							aria-label="Manage workspace"
							title="Manage workspace"
							onClick={(e) => {
								e.stopPropagation();
								setManageBoardsOpen(true);
							}}
						>
							<IconAdjustmentsHorizontal width={16} height={16} />
						</ActionIcon>
					</Tooltip>
					<Tooltip
						label="Create card in Workspace"
						openDelay={200}
						zIndex={9999}
						withArrow
					>
						<ActionIcon
							variant="subtle"
							size="md"
							color="var(--ui-color-text-muted)"
							aria-label="Create card in Workspace"
							disabled={isCreatingCard}
							onClick={async (e) => {
								e.stopPropagation();
								const newCardId = `Card-${nanoid()}`;

								let maxIndex = libraryCards?.length ?? 0;
								if (libraryCards) {
									for (const card of libraryCards) {
										const match = card.name?.match(/\d+/);
										if (match) {
											const num = Number.parseInt(match[0], 10);
											if (!Number.isNaN(num) && num > maxIndex) {
												maxIndex = num;
											}
										}
									}
								}
								const nextIndex =
									Math.max(maxIndex, highestCardIndexRef.current) + 1;
								highestCardIndexRef.current = nextIndex;

								const defaultName = `Card #${nextIndex}`;
								const createdCard = await createCardInWorkspace({
									section: navbarSection,
									name: defaultName,
									cardId: newCardId,
								});

								if (createdCard?.key) {
									requestRename(createdCard.key);
								}
							}}
						>
							<IconCardPlus width={16} height={16} />
						</ActionIcon>
					</Tooltip>
					<Tooltip label="New Folder" openDelay={200} zIndex={9999} withArrow>
						<ActionIcon
							variant="subtle"
							size="md"
							color="var(--ui-color-text-muted)"
							aria-label="New folder"
							disabled={isCreatingFolder}
							onClick={async (e) => {
								e.stopPropagation();
								const createdFolderId = await handleCreateFolder(null, {
									id: nanoid(),
									isFolder: true,
									name: "New Folder",
								});

								if (createdFolderId) {
									requestRename(createdFolderId);
								}
							}}
						>
							<IconFolderPlus width={16} height={16} />
						</ActionIcon>
					</Tooltip>
				</div>
			)}

			{(hideSectionHeader || !isCollapsed) && rootItems.length > 0 && (
				<div data-nav-tree-container="true">
					<Tree
						useBranchItems={useNavBranchItems}
						renderItem={renderNavTreeItem}
						indentPerLevel={TREE_INDENT_PER_LEVEL_PX}
						dragAndDropEnabled={treeDragAndDropEnabled}
						initialOpenState={initialOpenState}
						onOpenStateChange={(id, isOpen) => {
							const state = readTreeOpenState(OPEN_STATE_KEY);
							state[id] = isOpen;
							localStorage.setItem(OPEN_STATE_KEY, JSON.stringify(state));
						}}
						onMoveItem={handleMoveItem}
						onCreateFolder={handleCreateFolder}
						onDeleteFolder={handleDeleteFolder}
						renderLoading={() => <NavSectionLoadingItem />}
					/>
				</div>
			)}

			{hideSectionHeader && rootItems.length === 0 && (
				<div className="flex flex-col items-center justify-center py-10 text-center text-text-muted px-4">
					<p className="text-xs font-medium">No boards or folders yet</p>
					<p className="text-[11px] text-text-secondary mt-1">
						Create a new board using the + button above to get started.
					</p>
				</div>
			)}

			<MoveToFolderModal
				opened={moveModalItemId !== null}
				onClose={() => setMoveModalItemId(null)}
				itemKey={moveModalItemId ?? ""}
				sourceParentKey={moveModalSourceParent}
				section={navbarSection}
			/>

			<ManageBoardsModal
				opened={manageBoardsOpen}
				onClose={() => setManageBoardsOpen(false)}
				section={navbarSection}
			/>
		</div>
	);
}
